'use client';

import {useState} from 'react';
import {useLocale, useTranslations} from 'next-intl';
import Card from '@/components/ui/Card';
import Button from '@/components/ui/Button';

type Mode = 'login' | 'register';

export default function AuthPage() {
  const locale = useLocale();
  const t = useTranslations('auth');
  const [mode, setMode] = useState<Mode>('login');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [donType, setDonType] = useState('individu');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    const form = new FormData(e.currentTarget);
    const email = form.get('email') as string;
    const password = form.get('password') as string;

    try {
      const endpoint = mode === 'login' ? '/api/v1/auth/login' : '/api/v1/auth/register';
      const body = mode === 'login'
        ? {email, password}
        : {
            nom: form.get('nom') as string,
            email,
            password,
            telephone: form.get('telephone') as string,
            type: form.get('type') as string || 'individu',
            pays: form.get('pays') as string || 'TUN',
          };

      const apiUrl = process.env.NEXT_PUBLIC_API_URL || '';
      const res = await fetch(`${apiUrl}${endpoint}`, {
        method: 'POST',
        headers: {'Content-Type': 'application/json'},
        body: JSON.stringify(body),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || t('connection_error'));
      }

      const data = await res.json();
      if (data.access_token) {
        localStorage.setItem('utss_token', data.access_token);
        localStorage.setItem('utss_user', JSON.stringify({nom: data.nom, role: data.role, gouvernorat_id: data.gouvernorat_id, email: (form.get('email') as string)}));
        // Force password change on first login
        if (data.force_password_change) {
          window.location.href = `/${locale}/auth/change-password`;
          return;
        }
        // Redirect based on role
        const role = data.role;
        if (role === 'donateur') {
          window.location.href = `/${locale}/espace-donateur`;
        } else {
          window.location.href = `/${locale}/dashboard`;
        }
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[60vh] flex items-center justify-center px-4 py-16">
      <Card className="w-full max-w-md">
        {/* Tabs */}
        <div className="flex border-b border-border mb-6">
          <button
            onClick={() => {setMode('login'); setError('');}}
            className={`flex-1 py-3 text-center font-medium transition-colors cursor-pointer ${
              mode === 'login' ? 'text-primary border-b-2 border-primary' : 'text-muted'
            }`}
          >
            {t('login_tab')}
          </button>
          <button
            onClick={() => {setMode('register'); setError('');}}
            className={`flex-1 py-3 text-center font-medium transition-colors cursor-pointer ${
              mode === 'register' ? 'text-primary border-b-2 border-primary' : 'text-muted'
            }`}
          >
            {t('register_tab')}
          </button>
        </div>

        {error && (
          <div className="mb-4 p-3 rounded-lg bg-accent/10 text-accent text-sm">{error}</div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {mode === 'register' && (
            <>
              <div>
                <label htmlFor="nom" className="block text-sm font-medium mb-1">{t('full_name')}</label>
                <input id="nom" name="nom" type="text" required
                  className="w-full px-4 py-2.5 rounded-lg border border-border bg-white focus:ring-2 focus:ring-secondary/20 focus:border-secondary outline-none transition" />
              </div>
              <div>
                <label htmlFor="telephone" className="block text-sm font-medium mb-1">{t('phone')}</label>
                <input id="telephone" name="telephone" type="tel" placeholder="+216 XX XXX XXX"
                  className="w-full px-4 py-2.5 rounded-lg border border-border bg-white focus:ring-2 focus:ring-secondary/20 focus:border-secondary outline-none transition" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label htmlFor="type" className="block text-sm font-medium mb-1">{t('type')}</label>
                  <select id="type" name="type" value={donType} onChange={e => setDonType(e.target.value)} className="w-full px-3 py-2.5 rounded-lg border border-border bg-white focus:ring-2 focus:ring-secondary/20 focus:border-secondary outline-none transition text-sm">
                    <option value="individu">{t('individual')}</option>
                    <option value="entreprise">{t('company')}</option>
                  </select>
                </div>
                <div>
                  <label htmlFor="pays" className="block text-sm font-medium mb-1">{t('country')}</label>
                  <select id="pays" name="pays" className="w-full px-3 py-2.5 rounded-lg border border-border bg-white focus:ring-2 focus:ring-secondary/20 focus:border-secondary outline-none transition text-sm">
                    <option value="TUN">{t('country_TUN')}</option>
                    <option value="FRA">{t('country_FRA')}</option>
                    <option value="DEU">{t('country_DEU')}</option>
                    <option value="CAN">{t('country_CAN')}</option>
                    <option value="USA">{t('country_USA')}</option>
                    <option value="GBR">{t('country_GBR')}</option>
                    <option value="ARE">{t('country_ARE')}</option>
                    <option value="SAU">{t('country_SAU')}</option>
                    <option value="QAT">{t('country_QAT')}</option>
                    <option value="OTHER">{t('country_OTHER')}</option>
                  </select>
                </div>
              </div>
              {donType === 'entreprise' && (
                <div>
                  <label htmlFor="matricule_fiscal" className="block text-sm font-medium mb-1">{t('tax_id')}</label>
                  <input id="matricule_fiscal" name="matricule_fiscal" type="text" placeholder="0000000/X/X/X/000"
                    className="w-full px-4 py-2.5 rounded-lg border border-border bg-white focus:ring-2 focus:ring-secondary/20 focus:border-secondary outline-none transition text-sm" />
                </div>
              )}
            </>
          )}

          <div>
            <label htmlFor="email" className="block text-sm font-medium mb-1">{t('email')}</label>
            <input id="email" name="email" type="email" required
              className="w-full px-4 py-2.5 rounded-lg border border-border bg-white focus:ring-2 focus:ring-secondary/20 focus:border-secondary outline-none transition" />
          </div>

          <div>
            <label htmlFor="password" className="block text-sm font-medium mb-1">{t('password')}</label>
            <input id="password" name="password" type="password" required minLength={8}
              className="w-full px-4 py-2.5 rounded-lg border border-border bg-white focus:ring-2 focus:ring-secondary/20 focus:border-secondary outline-none transition" />
          </div>

          <Button type="submit" variant="primary" size="lg" className="w-full" disabled={loading}>
            {loading ? t('loading') : mode === 'login' ? t('sign_in') : t('create_account')}
          </Button>
        </form>

        {mode === 'login' && (
          <div className="text-center mt-4 space-y-2">
            <p className="text-xs text-muted">{t('login_hint')}</p>
            <button type="button" onClick={async () => {
              const email = prompt(t('enter_email'));
              if (!email) return;
              try {
                const res = await fetch('/api/v1/auth/reset-password-request', {method: 'POST', headers: {'Content-Type': 'application/json'}, body: JSON.stringify({email})});
                const data = await res.json();
                alert(data.message || t('request_sent'));
              } catch { alert(t('connection_error')); }
            }} className="text-xs text-primary hover:underline cursor-pointer">
              {t('forgot_password')}
            </button>
          </div>
        )}
        {mode === 'register' && (
          <p className="text-center text-xs text-muted mt-4 bg-surface p-2 rounded">
            {t('register_hint')}
          </p>
        )}
      </Card>
    </div>
  );
}
